import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import CoachFeedbackTable from "@/components/CoachFeedbackTable";

const Stat = ({ label, value, testId }) => (
  <div className="border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{label}</div>
    <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">{value}</div>
  </div>
);

export default function AvgsFeedbackResults() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/avgs-feedback/results").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);
  if (!data) return <div className="text-xs text-slate-400">Lädt…</div>;
  if (!data.total_sent) return (
    <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="avgs-results-empty">
      Noch keine AVGS-Feedbackbögen versendet. Der Bogen geht automatisch nach dem unterschriebenen Offboarding-Termin bzw. am Maßnahmeende raus.
    </div>
  );
  return (
    <div data-testid="avgs-results">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Antworten" value={`${data.total_submitted} / ${data.total_sent}`} testId="avgs-stat-responses" />
        <Stat label="Rücklaufquote" value={`${data.response_rate.toLocaleString("de-DE")} %`} testId="avgs-stat-rate" />
        <Stat label="Gesamt-Ø (1–5)" value={data.overall_avg != null ? data.overall_avg.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"} testId="avgs-stat-overall" />
      </div>
      <CoachFeedbackTable coaches={data.by_coach} questions={data.questions} />
      <div className="border border-slate-200 bg-white divide-y divide-slate-100 mb-6">
        {data.questions.map((q, i) => {
          const avg = data.avg_per_question[q.id];
          return (
            <div key={q.id} className="p-4 flex items-center gap-4" data-testid={`avgs-result-${q.id}`}>
              <div className="text-sm text-slate-800 flex-1 leading-snug">{i + 1}. {q.text}</div>
              <div className="w-28 h-1.5 bg-slate-100 shrink-0"><div className="h-full bg-slate-900" style={{ width: `${((avg || 0) / 5) * 100}%` }} /></div>
              <span className="text-xs font-mono text-slate-500 w-10 text-right">{avg != null ? avg.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}</span>
            </div>
          );
        })}
      </div>
      {data.comments.length > 0 && (
        <div className="border border-slate-200 bg-white" data-testid="avgs-comments">
          <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Freitext-Rückmeldungen</div>
          <ul className="divide-y divide-slate-100">
            {data.comments.map((c, i) => (
              <li key={i} className="p-4">
                <div className="text-[10px] font-mono text-slate-400 mb-1">{c.client_name} · {formatDeShort(c.submitted_at)}</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.comment}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
